import { useEffect, useRef, useState, useCallback } from 'react';
import { EventBus, useGameStore } from '@campus/core';
import { Button, Icon } from '../ds/index.js';

const AUTO_DISMISS_MS = 8000;
const KNOCK_TIMEOUT_MS = 30000;

type PromptPhase = 'ask' | 'waiting';

interface PromptState {
  bubbleId: string;
  phase: PromptPhase;
}

/**
 * KnockPrompt — devant une salle fermée : proposer de toquer, puis attendre
 * la réponse des personnes dans la salle (30 s max).
 */
export function KnockPrompt() {
  const bubbleNames = useGameStore((s) => s.bubbleNames);
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = useCallback(() => {
    setPrompt(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const onPrompt = ({ bubbleId }: { bubbleId: string }) => {
      setPrompt({ bubbleId, phase: 'ask' });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(close, AUTO_DISMISS_MS);
    };

    const onGranted = () => {
      EventBus.emit('ui:toast', { message: 'On t\u2019a ouvert, tu peux entrer.', tone: 'success' });
      close();
    };

    const onDenied = () => {
      EventBus.emit('ui:toast', { message: 'On ne t\u2019a pas ouvert.', tone: 'warning' });
      close();
    };

    EventBus.on('ui:knock-prompt', onPrompt);
    EventBus.on('knock:granted', onGranted);
    EventBus.on('knock:denied', onDenied);
    return () => {
      EventBus.off('ui:knock-prompt', onPrompt);
      EventBus.off('knock:granted', onGranted);
      EventBus.off('knock:denied', onDenied);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [close]);

  if (!prompt) return null;

  const handleKnock = () => {
    EventBus.emit('ui:knock-request', { bubbleId: prompt.bubbleId });
    setPrompt({ ...prompt, phase: 'waiting' });
    // Replace auto-dismiss with knock timeout
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      EventBus.emit('ui:toast', { message: 'Pas de r\u00e9ponse pour l\u2019instant.' });
      close();
    }, KNOCK_TIMEOUT_MS);
  };

  const room = bubbleNames.get(prompt.bubbleId) ?? 'Cette salle';

  return (
    <div className="pl-float-bottom">
      <div className="pl-pop" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px 10px 14px', fontSize: 13 }}>
        {prompt.phase === 'ask'
          ? <Icon name="lock" size={16} className="pl-menu-icon" />
          : <span className="pl-spinner" aria-hidden="true" />}
        <span style={{ flex: 1 }}>
          <strong>{room}</strong> est fermée.{prompt.phase === 'waiting' && ' Tu as toqué, en attente de réponse…'}
        </span>
        {prompt.phase === 'waiting' && <Button variant="ghost" size="sm" onClick={close}>Annuler</Button>}
      </div>
      {prompt.phase === 'ask' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={close}>Annuler</Button>
          <Button variant="primary" size="sm" onClick={handleKnock}>Toquer</Button>
        </div>
      )}
    </div>
  );
}
